/**
 * Deterministic A/B bucketing based on a session GUID.
 *
 * Uses the FNV-1a 32-bit hash to convert a GUID string into a uniformly
 * distributed unsigned integer, then maps that integer onto a 0-99 bucket.
 * The same GUID will always produce the same bucket/variant, which is the
 * core requirement for a valid A/B test (a returning session must always
 * see the same experience).
 */

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/**
 * Computes the 32-bit FNV-1a hash of a string.
 * Returns an unsigned 32-bit integer (0 to 4,294,967,295).
 */
export function fnv1a(input: string): number {
  let hash = FNV_OFFSET_BASIS;

  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // Multiply by the FNV prime using Math.imul to keep this a fast,
    // correctly-overflowing 32-bit multiplication.
    hash = Math.imul(hash, FNV_PRIME);
  }

  // Force to an unsigned 32-bit integer.
  return hash >>> 0;
}

/**
 * Normalizes a GUID string so that formatting differences (casing,
 * surrounding whitespace, braces) never change the resulting bucket.
 */
export function normalizeGuid(guid: string): string {
  return guid.trim().toLowerCase().replace(/[{}]/g, "");
}

/**
 * Maps a GUID to a stable bucket in the range 0-99 (inclusive).
 */
export function getBucket(guid: string): number {
  const normalized = normalizeGuid(guid);
  const hash = fnv1a(normalized);
  return hash % 100;
}

export type Variant = "A" | "B";

/**
 * Assigns a GUID to variant A or B.
 *
 * @param guid - the session GUID to bucket.
 * @param splitPercent - the percentage (0-100) of traffic assigned to
 * variant A. Defaults to 50 (a straight 50/50 split). A GUID whose bucket
 * falls below splitPercent gets 'A', otherwise 'B'.
 */
export function getVariant(guid: string, splitPercent = 50): Variant {
  return getBucket(guid) < splitPercent ? "A" : "B";
}

export interface BucketingResult {
  guid: string;
  normalizedGuid: string;
  hash: number;
  bucket: number;
  variant: Variant;
}

/**
 * Runs the full bucketing pipeline and returns every intermediate value,
 * useful for the interactive demo/debugging view.
 */
export function evaluateGuid(guid: string, splitPercent = 50): BucketingResult {
  const normalizedGuid = normalizeGuid(guid);
  const hash = fnv1a(normalizedGuid);
  const bucket = hash % 100;
  const variant: Variant = bucket < splitPercent ? "A" : "B";

  return { guid, normalizedGuid, hash, bucket, variant };
}
